import React, {useMemo} from "react";
import {useFormikContext} from "formik";
import {Button} from "@reside/ui";
import {isEqual} from "lodash";

import {useFormikIsFieldDirty} from "../../../../../hooks/useFormikIsFieldDirty";
import {FacilityPccMappingFormValues} from "./types";
import {usePccMappingFormContext} from "./PccMappingFormContext";
import {Footer} from "./styles";

type Props = Readonly<{
  workflowType: string;
  hasArbitrationWorkflow: boolean;
}>;

/**
 * Saves document upload category fields for one workflow section via PUT /pcc-mapping.
 * Must render inside FacilityPccMappingForm's Formik context.
 */
export const DocumentCategoryCardSaveButton = ({
  workflowType,
  hasArbitrationWorkflow,
}: Props) => {
  const {isInputDirty} = useFormikIsFieldDirty();
  const {isUpdating} = usePccMappingFormContext();
  const formik = useFormikContext<FacilityPccMappingFormValues>();

  const normalizedType = workflowType.toLowerCase();
  const isAdmission = normalizedType === "admission";

  const workflowFieldMeta = formik.getFieldMeta(
    `workflowCategoryMappings.${normalizedType}`,
  );

  const isDirty = useMemo(() => {
    if (isAdmission) {
      return (
        isInputDirty("defaultDocumentCategoryIdentifier") ||
        (!hasArbitrationWorkflow && isInputDirty("arbitrationCategoryId"))
      );
    }

    return !isEqual(workflowFieldMeta.value, workflowFieldMeta.initialValue);
  }, [
    isAdmission,
    hasArbitrationWorkflow,
    isInputDirty,
    workflowFieldMeta.value,
    workflowFieldMeta.initialValue,
  ]);

  const pccConnected = Boolean(
    formik.values.pccOrganizationId && formik.values.pccFacilityId,
  );

  if (!pccConnected) {
    return null;
  }

  return (
    <Footer>
      <Button
        color="success"
        testId={`documentCategorySave-${normalizedType}`}
        disabled={!isDirty || isUpdating}
        onClick={() => formik.submitForm()}
      >
        {isUpdating ? "Saving..." : "Save"}
      </Button>
    </Footer>
  );
};
